/*
  Lazy loader for decorative autoplaying videos.

  These videos used to carry their <source src> in the HTML, so the browser began
  downloading the file during the initial page load even when the section sat far
  below the fold. On the homepage that was two videos and ~58 MB, neither of them
  visible on the first screen.

  Markup contract:

    <video data-sl-lazy-video preload="none" poster="..." autoplay muted loop playsinline>
      <source data-src="..." type="...">
    </video>

  The sources are hydrated the first time the video comes within 300px of the
  viewport, and playback then follows visibility. Until that moment the poster is
  what the shopper sees, which is also what a shopper without JavaScript gets — so
  every section using this must set a poster.

  A source may carry data-media with a media query. Those are resolved once, at
  hydration, and the ones that do not match are dropped before the browser picks a
  file — which is how a section ships one crop per breakpoint without the other
  crop ever being fetched. Because it resolves once, crossing the breakpoint after
  the video has loaded keeps the crop it started with.

  prefers-reduced-motion keeps the poster for good: the video is never fetched.
*/
(function () {
  var SELECTOR = 'video[data-sl-lazy-video]';

  function hydrate(video) {
    if (video.dataset.slLazyVideoLoaded) return;
    video.dataset.slLazyVideoLoaded = 'true';

    var sources = video.querySelectorAll('source[data-src]');
    for (var i = 0; i < sources.length; i++) {
      var source = sources[i];
      var media = source.getAttribute('data-media');

      if (media && !window.matchMedia(media).matches) {
        source.parentNode.removeChild(source);
        continue;
      }

      source.setAttribute('src', source.getAttribute('data-src'));
      source.removeAttribute('data-src');
    }
    video.load();
  }

  function play(video) {
    hydrate(video);
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(function () {});
  }

  function bind(video) {
    /* Guards a second <script> tag for this asset and theme-editor re-renders. */
    if (video.dataset.slLazyVideoBound) return;
    video.dataset.slLazyVideoBound = 'true';

    /* Chrome only autoplays muted video; the attribute alone is not always enough. */
    video.muted = true;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.removeAttribute('autoplay');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      play(video);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            play(entry.target);
          } else if (entry.target.dataset.slLazyVideoLoaded) {
            entry.target.pause();
          }
        });
      },
      { rootMargin: '300px 0px', threshold: 0 }
    );

    observer.observe(video);
  }

  function init(root) {
    var videos = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < videos.length; i++) bind(videos[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
